/**
 * Sun shadow-map fit + throttle (issue #129). Two cheap levers on the one
 * DirectionalLight shadow rig set up in scene/lighting.ts:
 *
 *  1. **Fit** — instead of a static ±3 km ortho box pinned to the scene
 *     origin (which the camera routinely roams outside of, so it shadows the
 *     wrong ground and never frustum-culls anything), the box follows the
 *     camera's ground point and is clamped so it always stays over the AOI.
 *     A tighter box also lifts texel density, and lets the shadow camera cull
 *     casters beyond it.
 *  2. **Throttle** — `renderer.shadowMap.autoUpdate` is turned off and the map
 *     is re-rendered on an explicit cadence (every Nth frame) plus immediately
 *     on any camera jump / quality switch / layer toggle, rather than every
 *     frame. Rotor shadows step at the cadence rate; everything else is
 *     identical.
 *
 * Purely visual — this rig has NO bearing on any LOS result (hard constraint
 * #1: LOS truth is raypath's analytical raster, never a mesh/shadow read).
 * Atmosphere/shadows are cosmetic and must never influence a LOS display.
 *
 * The fit maths and the cadence state machine are pure and exported for unit
 * tests; the {@link ShadowRig} wrapper drives a three.js `DirectionalLight` +
 * `WebGLShadowMap` through narrow duck-typed interfaces so it is testable in
 * Node without a WebGLRenderer (the same split scene/lighting.ts uses).
 */

/** AOI extent in local viewer-space (x, z), used to clamp the follow box. */
export interface AoiBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/** Half-width of the (square) sun shadow ortho box, in metres. A ±2500 m box
 * (5 km across) keeps texel density at or above the pre-#129 static ±3 km box
 * on both Med (1024 px) and High (2048 px) tiers, comfortably contains the
 * #128 building shadow-cast radius (1200 m) plus the near, visually dominant
 * turbines/terrain, and culls the faded far field from the shadow pass. */
export const SHADOW_BOX_HALF_EXTENT_M = 2500;

/** Render the shadow map on every Nth frame when shadows are on. 2 = every
 * other frame (halves the shadow-pass frequency) while keeping rotor-shadow
 * stepping gentle; the issue permits 2–3. */
export const SHADOW_UPDATE_INTERVAL_FRAMES = 2;

/**
 * Clamps the camera ground point to a focus that keeps the whole ±halfExtent
 * box inside the AOI, so the shadow map never covers empty space beyond the
 * terrain. When the camera is central the focus is the camera point unchanged
 * (the box simply follows it); near or beyond an edge the focus stops
 * halfExtent short of that edge. If the AOI is narrower than the box on an
 * axis (degenerate), that axis falls back to the AOI centre.
 */
export function fitShadowFocus(
  cameraX: number,
  cameraZ: number,
  bounds: AoiBounds,
  halfExtent: number = SHADOW_BOX_HALF_EXTENT_M,
): { x: number; z: number } {
  return {
    x: clampBoxInside(cameraX, bounds.minX, bounds.maxX, halfExtent),
    z: clampBoxInside(cameraZ, bounds.minZ, bounds.maxZ, halfExtent),
  };
}

function clampBoxInside(v: number, min: number, max: number, halfExtent: number): number {
  const innerMin = min + halfExtent;
  const innerMax = max - halfExtent;
  if (innerMin > innerMax) return (min + max) / 2; // AOI narrower than the box on this axis
  if (v < innerMin) return innerMin;
  if (v > innerMax) return innerMax;
  return v;
}

/** Mutable cadence bookkeeping for {@link nextShadowUpdate}. */
export interface ShadowCadenceState {
  /** Frames elapsed since the last render (reset on each render). */
  frames: number;
  /** An event has requested the next enabled frame render regardless of cadence. */
  forced: boolean;
}

/** Fresh cadence, primed to render its first enabled frame (a scene must never
 * boot shadowless while waiting for a cadence beat). */
export function createShadowCadenceState(): ShadowCadenceState {
  return { frames: 0, forced: true };
}

/** Request an immediate render on the next enabled frame — a camera jump, a
 * live quality switch, or a layer toggle, none of which can wait for the
 * throttle (a stale shadow must never survive a teleport). */
export function requestShadowUpdate(state: ShadowCadenceState): void {
  state.forced = true;
}

/**
 * Advances the cadence one frame and returns whether the shadow map should
 * re-render this frame. While shadows are disabled it always returns false and
 * re-primes the `forced` flag, so the first frame after shadows come back
 * (a Low→Med/High switch, #73) renders immediately instead of on a stale beat.
 */
export function nextShadowUpdate(
  state: ShadowCadenceState,
  params: { shadowsEnabled: boolean; interval: number },
): boolean {
  if (!params.shadowsEnabled) {
    state.forced = true;
    state.frames = 0;
    return false;
  }
  if (state.forced) {
    state.forced = false;
    state.frames = 0;
    return true;
  }
  state.frames += 1;
  if (state.frames >= params.interval) {
    state.frames = 0;
    return true;
  }
  return false;
}

/** A three.js `Vector3`, reduced to what the rig touches. */
export interface Vec3Like {
  x: number;
  y: number;
  z: number;
  set(x: number, y: number, z: number): void;
}

/** A three.js `OrthographicCamera` (the shadow camera), reduced to its box. */
export interface ShadowOrthoCameraLike {
  left: number;
  right: number;
  top: number;
  bottom: number;
  updateProjectionMatrix(): void;
}

/** A three.js `DirectionalLight`, reduced to the shadow rig's touch points. */
export interface ShadowLightLike {
  position: Vec3Like;
  target: { position: Vec3Like };
  shadow: { camera: ShadowOrthoCameraLike };
}

/** A three.js `WebGLShadowMap`, reduced to the throttle controls. */
export interface ShadowMapControlLike {
  autoUpdate: boolean;
  needsUpdate: boolean;
}

export interface ShadowRigOptions {
  /** Half-width of the ortho box, in metres. Defaults to {@link SHADOW_BOX_HALF_EXTENT_M}. */
  halfExtent?: number;
  /** Render cadence in frames. Defaults to {@link SHADOW_UPDATE_INTERVAL_FRAMES}. */
  interval?: number;
  /** Ground height the box focuses on (local y). Defaults to 0, matching the
   * pre-#129 target at the origin. */
  targetY?: number;
}

/**
 * Drives a DirectionalLight's shadow so it follows the camera (a tighter,
 * AOI-clamped ortho box) and re-renders only on an explicit cadence. Holds the
 * fixed sun-direction offset captured at build time, so {@link refit} slides
 * the box without ever changing the beam direction or the box size.
 */
export class ShadowRig {
  private readonly light: ShadowLightLike;
  private readonly shadowMap: ShadowMapControlLike;
  private readonly bounds: AoiBounds;
  private readonly halfExtent: number;
  private readonly interval: number;
  private readonly targetY: number;
  private readonly offset: { x: number; y: number; z: number };
  private readonly cadence: ShadowCadenceState;

  constructor(
    light: ShadowLightLike,
    shadowMap: ShadowMapControlLike,
    bounds: AoiBounds,
    options: ShadowRigOptions = {},
  ) {
    this.light = light;
    this.shadowMap = shadowMap;
    this.bounds = bounds;
    this.halfExtent = options.halfExtent ?? SHADOW_BOX_HALF_EXTENT_M;
    this.interval = options.interval ?? SHADOW_UPDATE_INTERVAL_FRAMES;
    this.targetY = options.targetY ?? 0;
    // The sun direction, captured as the position→target offset. Preserving it
    // on every refit keeps lighting/shadows identical to the static rig.
    this.offset = {
      x: light.position.x - light.target.position.x,
      y: light.position.y - light.target.position.y,
      z: light.position.z - light.target.position.z,
    };
    this.cadence = createShadowCadenceState();

    // Throttle: shadows render only when we flag needsUpdate from here on.
    shadowMap.autoUpdate = false;
    // Fit: commit the box size exactly once. refit() only ever moves it.
    const cam = light.shadow.camera;
    cam.left = -this.halfExtent;
    cam.right = this.halfExtent;
    cam.top = this.halfExtent;
    cam.bottom = -this.halfExtent;
    cam.updateProjectionMatrix();
  }

  /**
   * Slides the shadow box so it follows the camera ground point, clamped to the
   * AOI. Position-only — the box keeps its build-time size, so this is cheap and
   * never grows the shadow frustum. Event-driven: call it on the tile-update
   * tick and on a camera teleport, never per frame (repo perf rule).
   */
  refit(cameraX: number, cameraZ: number): void {
    const focus = fitShadowFocus(cameraX, cameraZ, this.bounds, this.halfExtent);
    this.light.target.position.set(focus.x, this.targetY, focus.z);
    this.light.position.set(
      focus.x + this.offset.x,
      this.targetY + this.offset.y,
      focus.z + this.offset.z,
    );
  }

  /**
   * Per-frame cadence step. Flags the shadow map for a render on the cadence
   * beats (and on any forced event); otherwise leaves it alone so three.js
   * skips the whole shadow pass this frame. A no-op while shadows are off.
   */
  tick(shadowsEnabled: boolean): void {
    if (nextShadowUpdate(this.cadence, { shadowsEnabled, interval: this.interval })) {
      this.shadowMap.needsUpdate = true;
    }
  }

  /** Force the next enabled {@link tick} to render — camera jump, quality
   * switch, or layer toggle. */
  requestUpdate(): void {
    requestShadowUpdate(this.cadence);
  }
}
